import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faFilter } from '@fortawesome/pro-regular-svg-icons';
import { cn } from '@v2/shared/lib/utils';
import FilterGroup from './FilterGroup';
import { CategoryFacet, CategoryFacetValue } from './types';

type Props = {
  facets: CategoryFacet[];
  selectedCount: number;
  onNavigate: (value: CategoryFacetValue, parameter: string) => void;
  /** Rendered inside the panel below `lg`, where the toolbar has no room for it. */
  mobileControls?: React.ReactNode;
};

/**
 * The left-hand filter bar.
 *
 * Built to match `shared/Layouts/SideMenu`'s rail rather than as a new kind of thing:
 * same card (`rounded-xl` over `border-slate-200` and a soft shadow), same group
 * headings, same left-edge marker on the selected row, and the same behaviour below
 * `lg` — a labelled toggle that opens the panel in place. The institutional and account
 * pages already read that way, and "the left menu should match our other sidebars" was
 * the brief.
 *
 * It is not `SideMenu` itself. That one takes a flat list of links and owns the page's
 * two-column grid; this one nests (a category's children sit under it), needs the
 * selected-count on its toggle, and shares its panel with the sort control on the phone.
 * The shared parts are the classes and the shape, which is where the agreement matters.
 */
export default function FilterSidebar({ facets, selectedCount, onNavigate, mobileControls }: Props) {
  const [isOpen, setIsOpen] = useState(false);

  if (facets.length === 0 && !mobileControls) {
    return null;
  }

  return (
    <nav aria-label="Filter magazines" className="lg:sticky lg:top-6">
      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((open) => !open)}
        className={cn(
          'lg:hidden w-full flex items-center justify-between gap-3 cursor-pointer',
          'rounded-xl border border-solid border-slate-200 bg-white px-4 py-3',
          'text-left text-base font-semibold text-slate-800'
        )}
      >
        <span className="flex items-center gap-2">
          <FontAwesomeIcon icon={faFilter} className="text-slate-400" aria-hidden />
          Filter &amp; Sort
          {selectedCount > 0 && (
            <span className="rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-white">
              {selectedCount}
            </span>
          )}
        </span>

        <FontAwesomeIcon
          icon={faAngleDown}
          className={cn('text-slate-400 transition-transform', isOpen && 'rotate-180')}
          aria-hidden
        />
      </button>

      <div
        className={cn(
          'rounded-xl border border-solid border-slate-200 bg-white py-2 shadow-sm',
          'mt-2 lg:mt-0 lg:block',
          !isOpen && 'hidden'
        )}
      >
        {mobileControls && (
          <div className="border-0 border-b border-solid border-slate-100 px-5 py-3 lg:hidden">
            {mobileControls}
          </div>
        )}

        {/*
         * A wrapper of their own, so the first group is a first child. Beside the sort,
         * which is only hidden from `lg` up, it never was — and the first group kept the
         * rule meant to separate it from the one above, as a line over "Categories".
         */}
        <div>
          {facets.map((facet) => (
            <FilterGroup key={facet.parameter} facet={facet} onNavigate={onNavigate} />
          ))}
        </div>
      </div>
    </nav>
  );
}
